import {
  FUNNEL_EVENT_SEMANTICS,
  StorefrontSemanticActionSchema,
  normalizeEventName,
  type FunnelEventSemanticDefinition,
} from '@trafilea/tss-lib-funnel-schema-ts';
import { createEventEnvelope, type UIEventPayload } from '../events/envelope';
import { getActionHandler } from './registry';
import type { ActionExecutionContext, ActionExecutionResult } from './types';

const DEFAULT_SEMANTIC_TIMEOUT_MS = 15_000;
const MAX_COMPLETED_EVENT_IDS = 1_000;

interface DispatchState {
  inFlight: Map<string, Promise<ActionExecutionResult>>;
  completed: Set<string>;
  completedOrder: string[];
}

const stateByBus = new WeakMap<ActionExecutionContext['eventBus'], DispatchState>();

const semanticByActionType = new Map<string, FunnelEventSemanticDefinition>(
  Object.values(FUNNEL_EVENT_SEMANTICS)
    .filter((semantic) => semantic.actionType !== null)
    .map((semantic) => [semantic.actionType!, semantic])
);

function stateFor(ctx: ActionExecutionContext): DispatchState {
  const existing = stateByBus.get(ctx.eventBus);
  if (existing) return existing;
  const created: DispatchState = {
    inFlight: new Map(),
    completed: new Set(),
    completedOrder: [],
  };
  stateByBus.set(ctx.eventBus, created);
  return created;
}

function rememberCompleted(state: DispatchState, key: string): void {
  state.completed.add(key);
  state.completedOrder.push(key);
  while (state.completedOrder.length > MAX_COMPLETED_EVENT_IDS) {
    const oldest = state.completedOrder.shift();
    if (oldest) state.completed.delete(oldest);
  }
}

function normalizeResult(result: ActionExecutionResult | void): ActionExecutionResult {
  if (result?.status) return result;
  if (result?.unsupported) {
    return { ...result, status: 'unsupported' };
  }
  return { ...result, status: 'succeeded' };
}

function emitSemanticResult(
  ctx: ActionExecutionContext,
  semantic: FunnelEventSemanticDefinition,
  result: ActionExecutionResult
): void {
  const intent = ctx.intentEnvelope;
  const payload: UIEventPayload = {
    params: result.resultParams ?? {},
    context: intent?.payload.context ?? {
      stepId: ctx.stepId,
    },
  };

  ctx.eventBus.emit(
    createEventEnvelope(semantic.eventName, 'storefront-funnel-handler', payload, {
      phase: 'result',
      origin: 'handler',
      // Result and intent have independent event IDs. correlationId links
      // them without sharing a deduplication domain.
      correlationId: intent?.metadata.eventId ?? ctx.sessionId,
      sessionId: intent?.metadata.sessionId ?? ctx.sessionId,
      scope: intent?.metadata.scope ?? (ctx.tenantId ? { tenantId: ctx.tenantId } : undefined),
      hopCount: intent?.metadata.hopCount,
    })
  );
}

async function executeWithTimeout(
  ctx: ActionExecutionContext,
  timeoutMs: number
): Promise<ActionExecutionResult> {
  const handler = getActionHandler(String(ctx.action.type));
  if (!handler) {
    return {
      status: 'unsupported',
      unsupported: true,
      reason: 'missing_handler',
    };
  }

  const controller = new AbortController();
  const onAbort = () => controller.abort(ctx.signal.reason);
  if (ctx.signal.aborted) {
    controller.abort(ctx.signal.reason);
  } else {
    ctx.signal.addEventListener('abort', onAbort, { once: true });
  }

  let timeout: ReturnType<typeof setTimeout> | undefined;
  try {
    const result = await Promise.race([
      Promise.resolve(handler.execute({ ...ctx, signal: controller.signal })),
      new Promise<ActionExecutionResult>((resolve) => {
        timeout = setTimeout(() => {
          controller.abort(new Error('semantic action timed out'));
          resolve({
            status: 'failed',
            reason: 'timeout',
          });
        }, timeoutMs);
      }),
    ]);
    return normalizeResult(result);
  } catch (error) {
    ctx.logger.error('Funnel action handler failed', error instanceof Error ? error : undefined, {
      actionType: ctx.action.type,
      actionId: ctx.action.id,
      stepId: ctx.stepId,
    });
    return {
      status: 'failed',
      reason: ctx.signal.aborted ? 'aborted' : 'handler_exception',
    };
  } finally {
    if (timeout) clearTimeout(timeout);
    ctx.signal.removeEventListener('abort', onAbort);
  }
}

async function dispatchSemanticAction(
  ctx: ActionExecutionContext,
  semantic: FunnelEventSemanticDefinition
): Promise<ActionExecutionResult> {
  const parsed = StorefrontSemanticActionSchema.safeParse(ctx.action);
  if (!parsed.success) {
    ctx.logger.warn('Semantic action failed schema validation', {
      actionType: ctx.action.type,
      actionId: ctx.action.id,
      stepId: ctx.stepId,
      issues: parsed.error.issues.map((issue) => issue.message).slice(0, 5),
    });
    return {
      status: 'failed',
      reason: 'invalid_semantic_action',
    };
  }

  const intent = ctx.intentEnvelope;
  if (intent) {
    if (intent.metadata.phase === 'result') {
      return {
        status: 'failed',
        reason: 'result_cannot_execute_command',
      };
    }
    // Managed bundles have an exact binding → listener → action identity and
    // therefore require their canonical intent. Legacy/authored listeners may
    // still translate an observation (for example spin result → add_to_cart).
    if (
      parsed.data.managedBehavior !== undefined &&
      (intent.metadata.phase !== 'intent' || normalizeEventName(intent.name) !== semantic.eventName)
    ) {
      return {
        status: 'failed',
        reason: 'managed_intent_action_mismatch',
      };
    }
  }

  const state = stateFor(ctx);
  const dispatchKey = intent ? `${intent.metadata.eventId}:${parsed.data.type}` : '';
  if (dispatchKey && state.completed.has(dispatchKey)) {
    return {
      status: 'succeeded',
      reason: 'duplicate_already_completed',
    };
  }
  if (dispatchKey) {
    const existing = state.inFlight.get(dispatchKey);
    if (existing) return existing;
  }

  const execution = (async () => {
    const result = await executeWithTimeout(
      { ...ctx, action: parsed.data as ActionExecutionContext['action'] },
      DEFAULT_SEMANTIC_TIMEOUT_MS
    );
    if (result.status === 'succeeded') {
      if (dispatchKey) rememberCompleted(state, dispatchKey);
      emitSemanticResult(ctx, semantic, result);
    }
    // A deferred cross-document action (currently start_checkout) deliberately
    // emits and deduplicates nothing here. The destination creates the one
    // result only after its own trusted success boundary.
    return result;
  })();

  if (dispatchKey) state.inFlight.set(dispatchKey, execution);
  try {
    return await execution;
  } finally {
    if (dispatchKey) state.inFlight.delete(dispatchKey);
  }
}

/**
 * The only action execution entry point. Semantic actions are validated,
 * deduplicated, bounded, and emit a result only after their capability
 * succeeds. Legacy local actions pass through the same registry without
 * creating semantic results.
 */
export async function dispatchAction(ctx: ActionExecutionContext): Promise<ActionExecutionResult> {
  const actionType = String(ctx.action.type);
  const semantic = semanticByActionType.get(actionType);
  if (semantic) {
    return dispatchSemanticAction(ctx, semantic);
  }
  return executeWithTimeout(ctx, DEFAULT_SEMANTIC_TIMEOUT_MS);
}

export function getSemanticDefinitionForAction(
  actionType: string
): FunnelEventSemanticDefinition | undefined {
  return semanticByActionType.get(actionType);
}
